import { Trans, useLingui } from '@lingui/react/macro';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Link } from 'react-router-dom';

import { useGetDateFNSLocale } from '@/locales/helpers';
import { useImageFallback } from '@/utils/hooks';

import { red400 } from '../../../theme/videa-style-tokens';

const useStyles = makeStyles((theme) => ({
  flexBox: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  flexItem: {
    padding: theme.spacing(1),
  },
  measurementImage: {
    width: 'auto',
    height: '300px',
  },
  dialogContent: {
    height: '100%',
    padding: `0 ${theme.spacing(6)} ${theme.spacing(4)}`,
  },
  imagesHeading: {
    textAlign: 'left',
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: '20px',
    margin: `${theme.spacing(2)} 0 ${theme.spacing(1.5)}`,
  },
}));

const RblMeasurementGuideDialog = ({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}) => {
  const classes = useStyles();
  const {
    i18n: { locale },
  } = useLingui();

  const { imgSrc: paImgSrc, onError: paOnError } = useImageFallback(
    `/assets/images/rblMeasurementGuide/pa_measurement_image-${locale}.png`,
    '/assets/images/rblMeasurementGuide/pa_measurement_image-en.png',
  );

  const { imgSrc: bwImgSrc, onError: bwOnError } = useImageFallback(
    `/assets/images/rblMeasurementGuide/bw_measurement_image-${locale}.png`,
    '/assets/images/rblMeasurementGuide/bw_measurement_image-en.png',
  );

  return (
    <Dialog
      disableEscapeKeyDown
      maxWidth="md"
      aria-labelledby="rbl-measurement-dialog"
      open={isOpen}
      keepMounted
      onClose={() => setIsOpen(false)}
    >
      <Box display="flex" justifyContent="space-between">
        <DialogTitle>
          <b>
            <Trans>How we calculate Radiographic Bone Level</Trans>
          </b>
        </DialogTitle>
        <DialogActions>
          <IconButton onClick={() => setIsOpen(false)} color="secondary">
            <CloseIcon />
          </IconButton>
        </DialogActions>
      </Box>

      <DialogContent className={classes.dialogContent}>
        <Typography className={classes.flexItem} gutterBottom>
          <Trans>
            {`Radiographic bone level measurements 
          (distance from CEJ to ABL) are `}{' '}
            <b>not</b>{' '}
            {` the same 
          as periodontal probing measurements (marginal
          gingiva to gingival sulcus floor), so those 
          numbers often will not match for a given tooth.`}
          </Trans>
        </Typography>
        <Typography className={classes.flexItem} gutterBottom>
          <Trans>
            {`Videa Perio Assist measures bone level in bitewings 
          `}{' '}
            <b>in mm</b> and in bone level in periapical radiographs
            <b> as a ratio</b> (%); both measurements are described below:
          </Trans>
        </Typography>
        <Box display="flex" justifyContent="space-around" textAlign="center">
          <Box>
            <Typography className={classes.flexItem}>
              <b>
                <u>
                  <Trans>Bitewings (BWs)</Trans>
                </u>
              </b>
            </Typography>
            <Typography className={classes.flexItem}>
              <Trans>
                {'Bone level measurement in '} <b>mm</b>:
              </Trans>
            </Typography>
            <Typography className={classes.flexItem}>{'CEJ - ABL '}</Typography>
            <Typography fontSize="11px">
              <Trans>CEJ = Cementoenamel Junction</Trans>
            </Typography>
            <Typography fontSize="11px">
              <Trans>ABL = Alveolar Bone Level</Trans>
            </Typography>
          </Box>
          <Box>
            <Typography className={classes.flexItem}>
              <b>
                <u>
                  <Trans>Periapicals (PAs)</Trans>
                </u>
              </b>
            </Typography>
            <Typography className={classes.flexItem}>
              <Trans>
                {'Bone level measurement as '} <b>ratio</b> {' (vs. apex):'}
              </Trans>
            </Typography>
            <Typography className={classes.flexItem}>
              <Trans>((CEJ - ABL) / (CEJ - apex)) x 100</Trans>
            </Typography>
          </Box>
        </Box>
        <Typography className={classes.imagesHeading}>
          <b>
            <Trans>How to read Radiographic Bone Level measurements</Trans>
          </b>
        </Typography>
        <Typography fontSize="11px">
          <Trans>
            Option A color system is shown below. Your current color setting may
            be different. Colors may be adjusted for your practice in{' '}
            <Link
              to="?rbl-color-settings-open=true"
              onClick={() => setIsOpen(false)}
            >
              RBL Color Settings
            </Link>
          </Trans>
        </Typography>
        <Typography fontSize="11px" fontWeight="800" color={red400}>
          <Trans>
            BW color thresholds have been updated as of April 5, 2023.
          </Trans>
        </Typography>
        <Box display="flex" justifyContent="space-between" textAlign="center">
          <Box width="45%">
            <Typography className={classes.flexItem}>
              <b>
                <u>
                  <Trans>Bitewings (BWs)</Trans>
                </u>
              </b>
            </Typography>
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
            <img
              className={classes.measurementImage}
              src={bwImgSrc}
              onError={bwOnError}
              alt="bw-rbl-measurement-guide"
            />

            <Typography fontSize="11px" textAlign="left">
              <Trans>
                Note: Exact RBL measurements for BWs may vary between images and
                may be less accurate in cases of poorly framed BWs, BWs with few
                teeth, or BWs with severe tooth structure loss.
              </Trans>
            </Typography>
          </Box>
          <Box width="45%">
            <Typography className={classes.flexItem}>
              <b>
                <u>
                  <Trans>Periapicals (PAs)</Trans>
                </u>
              </b>
            </Typography>
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
            <img
              className={classes.measurementImage}
              src={paImgSrc}
              onError={paOnError}
              alt="pa-rbl-measurement-guide"
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default RblMeasurementGuideDialog;
